import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { CheckCircle2, Clock3, CreditCard, Smartphone, TimerReset } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { useSocket } from '../contexts/SocketContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function playTing() {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const first = audioContext.createOscillator();
    const second = audioContext.createOscillator();
    const gain = audioContext.createGain();
    first.frequency.value = 880;
    second.frequency.value = 1320;
    gain.gain.setValueAtTime(0.0001, audioContext.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.2, audioContext.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, audioContext.currentTime + 0.45);
    first.connect(gain);
    second.connect(gain);
    gain.connect(audioContext.destination);
    first.start();
    second.start(audioContext.currentTime + 0.08);
    first.stop(audioContext.currentTime + 0.28);
    second.stop(audioContext.currentTime + 0.48);
  } catch (error) {
    console.warn('Payment confirmation sound unavailable', error);
  }
}

function formatTime(ms) {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(seconds / 60).toString().padStart(2, '0');
  const rest = (seconds % 60).toString().padStart(2, '0');
  return `${minutes}:${rest}`;
}

export default function PaymentDisplay() {
  const { socket } = useSocket();
  const [payment, setPayment] = useState({ status: 'IDLE' });
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    axios.get(`${API}/payments/active-counter`).then((response) => setPayment(response.data)).catch(() => setPayment({ status: 'IDLE' }));
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!socket) return undefined;
    const handleCreated = (nextPayment) => setPayment(nextPayment);
    const handleExpired = (nextPayment) => setPayment(nextPayment);
    const handleSuccess = (nextPayment) => {
      setPayment(nextPayment);
      playTing();
    };
    socket.on('payment_created', handleCreated);
    socket.on('payment_expired', handleExpired);
    socket.on('payment_success', handleSuccess);
    return () => {
      socket.off('payment_created', handleCreated);
      socket.off('payment_expired', handleExpired);
      socket.off('payment_success', handleSuccess);
    };
  }, [socket]);

  const remaining = useMemo(() => (payment?.expiresAt ? payment.expiresAt - now : 0), [payment, now]);
  const qrUrl = payment?.qrPayload
    ? `https://api.qrserver.com/v1/create-qr-code/?size=520x520&data=${encodeURIComponent(payment.qrPayload)}`
    : payment?.qrImageUrl || payment?.razorpayImageUrl;
  const status = payment?.status === 'PENDING' && payment?.expiresAt && remaining <= 0 ? 'EXPIRED' : (payment?.status || 'IDLE');
  const amount = Math.round(Number(payment?.amount || 0));

  return (
    <div className="min-h-screen overflow-hidden bg-[radial-gradient(circle_at_top_left,_#164e63,_transparent_35%),linear-gradient(135deg,#020617,#0f172a_45%,#111827)] px-4 py-6 text-white sm:px-8">
      <div className="mx-auto flex min-h-[calc(100vh-3rem)] max-w-6xl flex-col">
        <header className="mb-6 flex flex-col gap-4 rounded-3xl border border-white/10 bg-white/10 p-4 shadow-2xl backdrop-blur md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-2xl bg-white p-1 shadow-lg shadow-emerald-500/20">
              <img src="/logo.png" alt="K-Crunch logo" className="h-full w-full object-contain" />
            </div>
            <div>
              <h1 className="text-2xl font-black tracking-tight sm:text-3xl">K-Crunch UPI Pay</h1>
              <p className="text-sm text-slate-300">Scan the active cashier QR to complete payment.</p>
            </div>
          </div>
        </header>

        <main className="grid flex-1 place-items-center">
          {status === 'PENDING' && (
            <Card className="w-full max-w-5xl overflow-hidden rounded-[2rem] border-white/10 bg-white/10 text-white shadow-2xl backdrop-blur-xl">
              <CardContent className="grid gap-8 p-6 md:grid-cols-[360px_1fr] md:p-10">
                <div className="rounded-[1.75rem] bg-white p-4 shadow-2xl">
                  {qrUrl ? <img src={qrUrl} alt="UPI QR code" className="aspect-square w-full rounded-2xl object-contain" /> : <div className="grid aspect-square place-items-center rounded-2xl bg-slate-100 text-slate-500">QR loading</div>}
                  <div className="mt-4 flex items-center justify-center gap-2 rounded-2xl bg-slate-100 px-4 py-3 font-bold text-slate-800">
                    <Smartphone className="h-5 w-5" /> Scan with any UPI app
                  </div>
                </div>
                <div className="flex flex-col justify-center text-center md:text-left">
                  <div className="mb-5 inline-flex w-fit items-center gap-2 self-center rounded-full border border-emerald-400/30 bg-emerald-400/10 px-4 py-2 text-sm font-semibold text-emerald-200 md:self-start">
                    <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-300" /> Awaiting payment
                  </div>
                  <p className="text-sm uppercase tracking-[0.35em] text-slate-400">Amount due</p>
                  <div className="mt-2 bg-gradient-to-r from-emerald-300 to-cyan-300 bg-clip-text text-6xl font-black text-transparent sm:text-7xl">₹{amount}</div>
                  <p className="mt-4 text-lg text-slate-300">Customer: <span className="font-semibold text-white">{payment.customerName || 'Walk-in Customer'}</span></p>
                  <div className="mt-8 grid gap-3 sm:grid-cols-2">
                    <div className="rounded-3xl border border-white/10 bg-slate-950/40 p-4">
                      <Clock3 className="mx-auto mb-2 h-6 w-6 text-amber-300 md:mx-0" />
                      <p className="text-xs uppercase tracking-wider text-slate-400">Time left</p>
                      <p className="text-3xl font-black tabular-nums">{formatTime(remaining)}</p>
                    </div>
                    <div className="rounded-3xl border border-white/10 bg-slate-950/40 p-4">
                      <CreditCard className="mx-auto mb-2 h-6 w-6 text-cyan-300 md:mx-0" />
                      <p className="text-xs uppercase tracking-wider text-slate-400">Mode</p>
                      <p className="text-xl font-bold">UPI Auto Confirm</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {status === 'PAID' && (
            <div className="w-full max-w-2xl rounded-[2rem] border border-emerald-400/30 bg-emerald-400/10 p-10 text-center shadow-2xl backdrop-blur-xl">
              <CheckCircle2 className="mx-auto h-24 w-24 text-emerald-300" />
              <h2 className="mt-6 text-4xl font-black">Payment Confirmed</h2>
              <p className="mt-3 text-lg text-slate-200">Payment of ₹{amount} has been received.</p>
              {payment.order_number && <p className="mt-2 text-slate-300">Order #{payment.order_number} is now in the queue.</p>}
            </div>
          )}

          {(status === 'IDLE' || status === 'EXPIRED') && (
            <div className="w-full max-w-2xl rounded-[2rem] border border-white/10 bg-white/10 p-10 text-center shadow-2xl backdrop-blur-xl">
              <TimerReset className="mx-auto h-20 w-20 text-slate-400" />
              <h2 className="mt-6 text-4xl font-black">Waiting for cashier</h2>
              <p className="mt-3 text-slate-300">A fresh UPI QR will appear here as soon as the cashier starts a payment.</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
